import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Save, X, AlertCircle, ArrowLeft, Upload, Trash2 } from 'lucide-react';
import { ContactFormData } from '../../types/contacts.types';
import { toast } from 'react-toastify';
import { contactsService } from '../../services/api/contactsService';

const ContactsForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    designation: '',
    department: '',
    contact_number: '',
    phone: '',
    email: '',
    office_type: 'head_office',
    office_location: '',
    is_active: true,
    display_order: 0,
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImage, setExistingImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData | 'image', string>>>({});

  useEffect(() => {
    if (isEditMode && id) {
      fetchContact(parseInt(id));
    }
  }, [id, isEditMode]);

  const fetchContact = async (contactId: number) => {
    try {
      setFetchingData(true);
      const response = await contactsService.getById(contactId);
      if (response.success) {
        const data = response.data;
        setFormData({
          name: data.name || '',
          designation: data.designation || '',
          department: data.department || '',
          contact_number: data.contact_number || '',
          phone: data.phone || '',
          email: data.email || '',
          office_type: data.office_type,
          office_location: data.office_location || '',
          is_active: data.is_active,
          display_order: data.display_order || 0,
        });
        if (data.image) {
          setExistingImage(data.image);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch contact details');
      console.error(error);
      navigate('/contacts');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });

    if (errors[name as keyof ContactFormData]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setExistingImage(null);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setExistingImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ContactFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.designation.trim()) {
      newErrors.designation = 'Designation is required';
    }
    if (!formData.contact_number.trim()) {
      newErrors.contact_number = 'Contact number is required';
    } else if (!/^[0-9\s\-\+\(\)]+$/.test(formData.contact_number)) {
      newErrors.contact_number = 'Invalid contact number format';
    }
    if (formData.phone && !/^[0-9\s\-\+\(\)]*$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (formData.display_order < 0) {
      newErrors.display_order = 'Display order must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);

      if (isEditMode && id) {
        await contactsService.update(parseInt(id), formData, imageFile || undefined);
        toast.success('Contact updated successfully');
      } else {
        await contactsService.create(formData, imageFile || undefined);
        toast.success('Contact created successfully');
      }

      navigate('/contacts');
    } catch (error: any) {
      const message = error.response?.data?.message ||
        (isEditMode ? 'Failed to update contact' : 'Failed to create contact');
      toast.error(message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading contact data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Contact' : 'Create Contact'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEditMode
              ? 'Update contact information'
              : 'Add a new contact to the directory'}
          </p>
        </div>
        <Link
          to="/contacts"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to List
        </Link>
      </div>

      {/* Form Card */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photo (Optional)
            </label>
            <div className="flex items-start gap-4">
              {(imagePreview || existingImage) && (
                <div className="relative">
                  <img
                    src={imagePreview || existingImage || ''}
                    alt="Contact"
                    className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <Upload size={18} className="mr-2" />
                  {imagePreview || existingImage ? 'Change Image' : 'Upload Image'}
                </button>
                <p className="mt-2 text-xs text-gray-500">
                  JPEG, PNG, GIF or WebP. Max 5MB.
                </p>
              </div>
            </div>
          </div>

          {/* Name and Office Type Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.name
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="e.g., Sri P. Joga Reddy"
              />
              {errors.name && (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.name}</span>
                </div>
              )}
            </div>

            {/* Office Type */}
            <div>
              <label
                htmlFor="office_type"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Office Type <span className="text-red-500">*</span>
              </label>
              <select
                id="office_type"
                name="office_type"
                value={formData.office_type}
                onChange={handleChange}
                className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                <option value="head_office">Head Office</option>
                <option value="territory_office">Territory Office</option>
              </select>
            </div>
          </div>

          {/* Designation and Department Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Designation */}
            <div>
              <label
                htmlFor="designation"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Designation <span className="text-red-500">*</span>
              </label>
              <textarea
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                rows={3}
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors resize-none ${
                  errors.designation
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="e.g., Engineer-in-Chief, PR"
              />
              {errors.designation && (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.designation}</span>
                </div>
              )}
            </div>

            {/* Department */}
            <div>
              <label
                htmlFor="department"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Department (Optional)
              </label>
              <textarea
                id="department"
                name="department"
                value={formData.department || ''}
                onChange={handleChange}
                rows={3}
                className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors resize-none"
                placeholder="e.g., Department of Panchayat Raj Engineering"
              />
            </div>
          </div>

          {/* Contact Number, Phone, and Email Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Contact Number (Mobile) */}
            <div>
              <label
                htmlFor="contact_number"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="contact_number"
                name="contact_number"
                value={formData.contact_number}
                onChange={handleChange}
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.contact_number
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="e.g., 87128 67057"
              />
              {errors.contact_number && (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.contact_number}</span>
                </div>
              )}
            </div>

            {/* Phone (Landline) */}
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Phone / Landline (Optional)
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.phone
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="e.g., 040-23234567"
              />
              {errors.phone && (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.phone}</span>
                </div>
              )}
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email (Optional)
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.email
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="e.g., contact@example.com"
              />
              {errors.email && (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.email}</span>
                </div>
              )}
            </div>
          </div>

          {/* Office Location */}
          <div>
            <label
              htmlFor="office_location"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Office Location / Address (Optional)
            </label>
            <input
              type="text"
              id="office_location"
              name="office_location"
              value={formData.office_location}
              onChange={handleChange}
              className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              placeholder="e.g., SRTGN Bhavan, Errummanzil Colony, Hyderabad – 500 082"
            />
          </div>

          {/* Display Order and Active Status Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Display Order */}
            <div>
              <label
                htmlFor="display_order"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Display Order
              </label>
              <input
                type="number"
                id="display_order"
                name="display_order"
                value={formData.display_order}
                onChange={handleChange}
                min="0"
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.display_order
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              />
              {errors.display_order ? (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.display_order}</span>
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-500">
                  Lower numbers appear first in the list
                </p>
              )}
            </div>

            {/* Active Status */}
            <div className="flex items-end pb-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Active
                </span>
              </label>
              <span className="ml-2 text-sm text-gray-500">
                (Visible in contact directory)
              </span>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/contacts')}
              disabled={loading}
              className="inline-flex items-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={18} className="mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  {isEditMode ? 'Update Contact' : 'Create Contact'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactsForm;
